<template>
  <div>
    <CCard>
      <CCardHeader>
        <h4>Credit Card Charges</h4>
      </CCardHeader>
      <CCardBody>
        <CRow class="mb-4">
          <CCol md="6">
            <div class="d-flex align-items-center">
              <CInput
                v-model="filterValue"
                placeholder="Filter by Account ID or CC Last 4 Digits"
                class="flex-grow-1 mb-0"
                @keyup.enter="applyFilter"
              />
              <CButton
                color="primary"
                class="ml-2"
                :disabled="!isFilterValid && filterValue.length > 0"
                @click="applyFilter"
              >
                Filter
              </CButton>
              <CButton
                v-if="activeFilter"
                color="secondary"
                class="ml-2"
                @click="clearFilter"
              >
                Clear
              </CButton>
            </div>
          </CCol>
        </CRow>

        <div
          v-if="!isFilterValid && filterValue.length > 0"
          class="alert alert-warning mb-3"
        >
          <CIcon
            name="cil-warning"
            class="mr-2"
          />
          Invalid filter format. Please enter either:
          <ul class="mb-0 mt-1">
            <li>Account ID: starts with SACC or BACC followed by 32 characters (36 total)</li>
            <li>Last 4 Digits: exactly 4 digits</li>
          </ul>
        </div>

        <div
          v-if="isLoading"
          class="text-center p-5"
        >
          <CSpinner
            style="width:2rem;height:2rem;"
            color="info"
            grow
          />
        </div>

        <div v-else>
          <CDataTable
            small
            border
            sorter
            :items="allCharges"
            :fields="fields"
            :sorter-value="{ column: 'paymentDate', asc: false }"
          >
            <template #accountId="{ item }">
              <td>
                <router-link
                  v-if="item.accountId"
                  :to="getAccountDetailsRoute(item.accountId)"
                  target="_blank"
                >
                  {{ item.accountId }}
                  <CIcon
                    name="cil-external-link"
                    size="custom"
                    class="ml-1"
                  />
                </router-link>
                <span v-else>N/A</span>
              </td>
            </template>

            <template #paymentDate="{ item }">
              <td class="text-nowrap">
                {{ formatDateTimeWithYear(item.paymentDate) }}
              </td>
            </template>

            <template #amount="{ item }">
              <td class="text-right">
                {{ formatAmount(item.amount) }}
              </td>
            </template>

            <template #cardType="{ item }">
              <td>
                <CIcon
                  v-if="getCardIcon(item.cardType)"
                  :name="getCardIcon(item.cardType)"
                  size="xl"
                  :style="{ color: getCardColor(item.cardType) }"
                />
                <span v-else>{{ item.cardType ? item.cardType.toUpperCase() : '' }}</span>
              </td>
            </template>

            <template #actions="{ item }">
              <td>
                <CButton
                  size="sm"
                  color="info"
                  variant="outline"
                  @click="downloadReceipt(item.accountId, item.transactionId)"
                >
                  Download
                </CButton>
              </td>
            </template>
          </CDataTable>

          <div
            v-if="totalPages > 1"
            class="d-flex justify-content-center align-items-center mt-3"
          >
            <CButton
              color="secondary"
              size="sm"
              :disabled="currentPage === 1"
              @click="goToPage(currentPage - 1)"
            >
              Previous
            </CButton>
            <span class="mx-3">
              Page {{ currentPage }} of {{ totalPages }} ({{ totalCount }} records)
            </span>
            <CButton
              color="secondary"
              size="sm"
              :disabled="currentPage === totalPages"
              @click="goToPage(currentPage + 1)"
            >
              Next
            </CButton>
          </div>
          <div
            v-else-if="totalCount > 0"
            class="text-center text-muted mt-3"
          >
            {{ totalCount }} record(s)
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import { formatAmount } from '@/utils'
import { showErrorMessage } from '@/notification-utils'
import BillingAPI from '@/api/billing.api'

const ITEMS_PER_PAGE = 15
const ACCOUNT_ID_PATTERN = /^(SACC|BACC)[A-Za-z0-9]{32}$/
const LAST_4_DIGITS_PATTERN = /^\d{4}$/

export default {
  name: 'CreditCardCharges',

  title: 'Credit Card Charges',

  data() {
    return {
      isLoading: false,
      filterValue: '',
      activeFilter: '',
      currentPage: 1,
      fields: [
        { key: 'accountId', label: 'Account ID' },
        { key: 'paymentDate', label: 'Date' },
        { key: 'amount', label: 'Amount', _classes: 'text-right' },
        { key: 'transactionDescription', label: 'Description' },
        { key: 'cardType', label: 'Card Type' },
        { key: 'lastCreditCardDigits', label: 'Last 4 Digits' },
        { key: 'actions', label: 'Actions', sorter: false, filter: false }
      ]
    }
  },

  computed: {
    ...mapState('creditCards', ['allCharges', 'allChargesTotalCount']),

    totalCount() {
      return this.allChargesTotalCount
    },

    totalPages() {
      return Math.ceil(this.totalCount / ITEMS_PER_PAGE) || 1
    },

    isFilterValid() {
      if (!this.filterValue || this.filterValue.length === 0) {
        return true
      }

      return ACCOUNT_ID_PATTERN.test(this.filterValue) || LAST_4_DIGITS_PATTERN.test(this.filterValue)
    }
  },

  async created() {
    await this.loadCharges()
  },

  methods: {
    ...mapActions('creditCards', ['getAllCharges', 'getAllChargesTotal']),

    async loadCharges() {
      try {
        this.isLoading = true
        const skip = (this.currentPage - 1) * ITEMS_PER_PAGE
        const filter = this.activeFilter || undefined
        await Promise.all([
          this.getAllCharges({ filter, skip, limit: ITEMS_PER_PAGE }),
          this.getAllChargesTotal(filter)
        ])
      } catch (e) {
        // Error handling done by API layer
      } finally {
        this.isLoading = false
      }
    },

    async applyFilter() {
      if (!this.isFilterValid) {
        return
      }

      this.activeFilter = this.filterValue
      this.currentPage = 1
      await this.loadCharges()
    },

    async clearFilter() {
      this.filterValue = ''
      this.activeFilter = ''
      this.currentPage = 1
      await this.loadCharges()
    },

    async goToPage(page) {
      if (page < 1 || page > this.totalPages) {
        return
      }

      this.currentPage = page
      await this.loadCharges()
    },

    async downloadReceipt(accountId, transactionId) {
      try {
        const response = await BillingAPI.generateDownloadLink({
          accountId,
          invoiceId: transactionId
        })
        window.open(response.data)
      } catch (e) {
        showErrorMessage(e)
      }
    },

    getAccountDetailsRoute(accountId) {
      if (accountId.startsWith('BACC')) {
        return { name: 'BuyerAccountDetails', params: { id: accountId } }
      } else if (accountId.startsWith('SACC')) {
        return { name: 'SellerAccountDetails', params: { id: accountId } }
      }

      return { name: 'BuyerAccountDetails', params: { id: accountId } }
    },

    getCardIcon(cardType) {
      if (!cardType) {
 return null 
}

      const type = cardType.toLowerCase()
      const iconMap = {
        visa: 'cib-cc-visa',
        master: 'cib-cc-mastercard',
        mastercard: 'cib-cc-mastercard',
        amex: 'cib-cc-amex',
        'american express': 'cib-cc-amex',
        american_express: 'cib-cc-amex',
        discover: 'cib-cc-discover'
      }
      return iconMap[type] || null
    },

    getCardColor(cardType) {
      if (!cardType) {
 return null 
}

      const type = cardType.toLowerCase()
      const colorMap = {
        visa: '#1A1F71',
        master: '#EB001B',
        mastercard: '#EB001B',
        amex: '#006FCF',
        'american express': '#006FCF',
        american_express: '#006FCF',
        discover: '#FF6000'
      }
      return colorMap[type] || null
    },

    formatAmount,

    formatDateTimeWithYear(date) {
      return moment(new Date(date)).format('MMM DD, YYYY hh:mm:ss A')
    }
  }
}
</script>
